import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  forwardRef,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { ConnectedPosition, OverlayContainer, OverlayModule } from '@angular/cdk/overlay';
import {
  DEFAULT_THEME_BACKGROUND,
  THEME_COLOR_PRESETS,
  ThemeCustomSwatch,
  hexToHsv,
  hsvToHex,
  loadCustomThemeColors,
  resolveThemeBackground,
  saveCustomThemeColors,
  sanitizeThemeBackground,
  stableCustomThemeId,
  themeBackgroundsEqual,
} from '../../utils/theme-color.util';

interface PaletteSwatch {
  id: string;
  bg: string;
  title: string;
  isCustom: boolean;
}

/** Prefer below the + control; flip above when there is no room. */
const POPOVER_POSITIONS: ConnectedPosition[] = [
  { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 14 },
  { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -14 },
];

/** Raised only while open — above prepaid modal (1001), below app dialogs (10000+). */
const OVERLAY_RAISE_CLASS = 'tcp-color-picker-overlay';

/** Shared CDK overlay container raise — ref-counted across picker instances. */
let overlayRaiseCount = 0;

@Component({
  selector: 'app-theme-color-picker',
  standalone: true,
  imports: [CommonModule, OverlayModule],
  templateUrl: './theme-color-picker.component.html',
  styleUrls: ['./theme-color-picker.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ThemeColorPickerComponent),
      multi: true,
    },
  ],
})
export class ThemeColorPickerComponent implements ControlValueAccessor, OnInit, OnDestroy {
  @Input() label = 'Theme color';
  @Input() required = true;
  @Input() helperText =
    'Pick a preset or create a custom solid/gradient — saved colors stay in your palette for any package.';

  readonly presets = THEME_COLOR_PRESETS;
  readonly popoverPositions = POPOVER_POSITIONS;
  custom: ThemeCustomSwatch[] = [];
  selectedId: string = THEME_COLOR_PRESETS[0].id;
  value: string = DEFAULT_THEME_BACKGROUND;
  disabled = false;

  pickerOpen = false;
  mode: 'solid' | 'gradient' = 'solid';
  activeStop: 0 | 1 = 0;
  /** Two hex stops used when mode === 'gradient' (matches preset 180deg style). */
  gradStops: [string, string] = ['#0060D8', '#30A8D8'];
  h = 210;
  s = 0.72;
  v = 0.9;
  hexInput = '';

  private onChange: (value: string) => void = () => undefined;
  private onTouched: () => void = () => undefined;
  private dragKind: 'sv' | 'hue' | null = null;
  private dragEl: HTMLElement | null = null;
  private customsLoaded = false;
  private overlayRaiseHeld = false;
  private readonly onScrollClose = (): void => this.setPickerOpen(false);
  private scrollCloseTargets: EventTarget[] = [];

  constructor(
    private readonly host: ElementRef<HTMLElement>,
    private readonly cdr: ChangeDetectorRef,
    private readonly overlayContainer: OverlayContainer
  ) {}

  ngOnInit(): void {
    this.custom = loadCustomThemeColors();
    this.customsLoaded = true;
    this.ensureExistingColorInPalette(this.value);
    this.syncSelectedIdFromValue();
    this.syncPickerControlsFromValue(this.value);
  }

  ngOnDestroy(): void {
    this.endDrag();
    this.detachScrollCloseListeners();
    this.setOverlayRaised(false);
  }

  get swatches(): PaletteSwatch[] {
    return [
      ...this.presets.map(p => ({ ...p, isCustom: false })),
      ...this.custom.map(c => ({ ...c, isCustom: true })),
    ];
  }

  get hueBase(): string {
    return hsvToHex(this.h, 1, 1);
  }

  get previewColor(): string {
    return this.mode === 'solid'
      ? hsvToHex(this.h, this.s, this.v)
      : `linear-gradient(180deg,${this.gradStops[0]},${this.gradStops[1]})`;
  }

  get svX(): number {
    return this.s * 100;
  }

  get svY(): number {
    return (1 - this.v) * 100;
  }

  get huePct(): number {
    return (this.h / 360) * 100;
  }

  writeValue(value: string | null): void {
    this.value = (value && String(value).trim()) || DEFAULT_THEME_BACKGROUND;
    if (this.customsLoaded) {
      this.ensureExistingColorInPalette(this.value);
    }
    this.syncSelectedIdFromValue();
    this.syncPickerControlsFromValue(this.value);
    this.cdr.markForCheck();
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.cdr.markForCheck();
  }

  trackSwatch(_index: number, sw: PaletteSwatch): string {
    return sw.id;
  }

  selectSwatch(sw: PaletteSwatch, event?: Event): void {
    this.stop(event);
    if (this.disabled) {
      return;
    }
    this.commitValue(sw.id, sw.bg, true);
  }

  openPicker(event?: Event): void {
    this.stop(event);
    if (this.disabled) {
      return;
    }
    this.setPickerOpen(true);
    this.onTouched();
  }

  closePicker(event?: Event): void {
    this.stop(event);
    this.setPickerOpen(false);
  }

  setMode(mode: 'solid' | 'gradient', event?: Event): void {
    this.stop(event);
    if (this.mode === mode) {
      return;
    }
    this.mode = mode;
    if (mode === 'gradient') {
      this.writeActiveStop(hsvToHex(this.h, this.s, this.v));
    }
    this.cdr.markForCheck();
  }

  selectStop(stop: 0 | 1, event?: Event): void {
    this.stop(event);
    if (this.activeStop === stop) {
      return;
    }
    this.activeStop = stop;
    const hsv = hexToHsv(this.gradStops[stop]);
    if (hsv) {
      this.applyHsv(hsv.h, hsv.s, hsv.v, false);
    }
    this.cdr.markForCheck();
  }

  onHexInput(event: Event): void {
    const raw = (event.target as HTMLInputElement).value.replace(/[^0-9a-fA-F]/g, '').slice(0, 6);
    this.hexInput = raw;
    const hsv = hexToHsv(raw);
    if (hsv) {
      this.applyHsv(hsv.h, hsv.s, hsv.v);
    }
  }

  onSvPointerDown(event: PointerEvent): void {
    this.beginDrag('sv', event);
  }

  onHuePointerDown(event: PointerEvent): void {
    this.beginDrag('hue', event);
  }

  @HostListener('document:pointermove', ['$event'])
  onPointerMove(event: PointerEvent): void {
    if (!this.dragKind || !this.dragEl) {
      return;
    }
    if (this.dragKind === 'sv') {
      this.updateSv(event);
    } else {
      this.updateHue(event);
    }
    this.cdr.markForCheck();
  }

  @HostListener('document:pointerup')
  onPointerUp(): void {
    this.endDrag();
  }

  addCustom(event?: Event): void {
    this.stop(event);
    const candidate =
      this.mode === 'solid'
        ? hsvToHex(this.h, this.s, this.v)
        : `linear-gradient(180deg,${this.gradStops[0]},${this.gradStops[1]})`;
    const safe = sanitizeThemeBackground(candidate);
    if (!safe) {
      return;
    }
    const existing = this.custom.find(c => themeBackgroundsEqual(c.bg, safe));
    if (existing) {
      this.commitValue(existing.id, existing.bg, true);
      return;
    }
    const item: ThemeCustomSwatch = {
      id: 'c' + Date.now(),
      bg: safe,
      title: 'Custom ' + safe,
    };
    this.custom = [...this.custom, item];
    saveCustomThemeColors(this.custom);
    this.commitValue(item.id, item.bg, true);
  }

  deleteCustom(id: string, event: Event): void {
    this.stop(event);
    if (this.disabled || !id.startsWith('c')) {
      return;
    }
    const removed = this.custom.find(c => c.id === id);
    this.custom = this.custom.filter(c => c.id !== id);
    saveCustomThemeColors(this.custom);
    if (removed && this.selectedId === id) {
      const fallback = this.presets[0];
      this.commitValue(fallback.id, fallback.bg, false);
    }
    this.cdr.markForCheck();
  }

  private stop(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
  }

  private commitValue(id: string, bg: string, closePicker: boolean): void {
    this.selectedId = id;
    this.value = bg;
    this.syncPickerControlsFromValue(bg);
    this.onChange(bg);
    this.onTouched();
    if (closePicker) {
      this.setPickerOpen(false);
    }
  }

  /** Keep HSV / gradient editor in sync with the committed form value. */
  private syncPickerControlsFromValue(raw: string): void {
    const resolved = resolveThemeBackground(raw) || DEFAULT_THEME_BACKGROUND;
    const stops = (resolved.match(/#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})\b/g) || []).map(hex => {
      const h = hex.slice(1);
      return h.length === 3
        ? `#${h[0]}${h[0]}${h[1]}${h[1]}${h[2]}${h[2]}`.toUpperCase()
        : `#${h}`.toUpperCase();
    });
    if (stops.length >= 2) {
      this.mode = 'gradient';
      this.gradStops = [stops[0], stops[1]];
      this.activeStop = 0;
      const hsv = hexToHsv(stops[0]);
      if (hsv) {
        this.applyHsv(hsv.h, hsv.s, hsv.v, false);
      }
    } else if (stops.length === 1) {
      this.mode = 'solid';
      const hsv = hexToHsv(stops[0]);
      if (hsv) {
        this.applyHsv(hsv.h, hsv.s, hsv.v, false);
      }
    }
    this.syncHexFromHsv();
  }

  private writeActiveStop(hex: string): void {
    this.gradStops = this.activeStop === 0 ? [hex, this.gradStops[1]] : [this.gradStops[0], hex];
  }

  private beginDrag(kind: 'sv' | 'hue', event: PointerEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.dragKind = kind;
    this.dragEl = event.currentTarget as HTMLElement;
    if (kind === 'sv') {
      this.updateSv(event);
    } else {
      this.updateHue(event);
    }
  }

  private setPickerOpen(open: boolean): void {
    if (this.pickerOpen === open) {
      return;
    }
    this.pickerOpen = open;
    this.setOverlayRaised(open);
    if (open) {
      this.attachScrollCloseListeners();
    } else {
      this.detachScrollCloseListeners();
    }
    this.cdr.markForCheck();
  }

  /**
   * Prepaid modal is z-index 1001; CDK overlay defaults to 1000 (panel would open behind it).
   * Ref-count the raise class so destroying/closing one picker doesn't drop another still open.
   */
  private setOverlayRaised(raised: boolean): void {
    if (raised === this.overlayRaiseHeld) {
      return;
    }
    const el = this.overlayContainer.getContainerElement();
    this.overlayRaiseHeld = raised;
    if (raised) {
      overlayRaiseCount++;
      if (overlayRaiseCount === 1) {
        el.classList.add(OVERLAY_RAISE_CLASS);
      }
    } else {
      overlayRaiseCount = Math.max(0, overlayRaiseCount - 1);
      if (overlayRaiseCount === 0) {
        el.classList.remove(OVERLAY_RAISE_CLASS);
      }
    }
  }

  /** Scroll does not bubble — listen on overflow ancestors (e.g. modal-body). */
  private attachScrollCloseListeners(): void {
    this.detachScrollCloseListeners();
    let node: HTMLElement | null = this.host.nativeElement.parentElement;
    while (node && node !== document.documentElement) {
      const { overflow, overflowY } = getComputedStyle(node);
      if (
        overflow === 'auto' ||
        overflow === 'scroll' ||
        overflowY === 'auto' ||
        overflowY === 'scroll'
      ) {
        node.addEventListener('scroll', this.onScrollClose, { passive: true });
        this.scrollCloseTargets.push(node);
      }
      node = node.parentElement;
    }
  }

  private detachScrollCloseListeners(): void {
    for (const target of this.scrollCloseTargets) {
      target.removeEventListener('scroll', this.onScrollClose);
    }
    this.scrollCloseTargets = [];
  }

  private syncSelectedIdFromValue(): void {
    const match = this.swatches.find(s => themeBackgroundsEqual(s.bg, this.value));
    this.selectedId = match?.id ?? this.presets[0].id;
  }

  /**
   * Existing packages may use legacy tokens or colors outside the default presets.
   * Promote that color into the custom palette so it stays selectable.
   */
  private ensureExistingColorInPalette(rawValue: string): void {
    const resolved = resolveThemeBackground(rawValue);
    if (!resolved) {
      return;
    }
    if (
      this.presets.some(p => themeBackgroundsEqual(p.bg, resolved)) ||
      this.custom.some(c => themeBackgroundsEqual(c.bg, resolved))
    ) {
      return;
    }
    this.custom = [
      ...this.custom,
      {
        id: stableCustomThemeId(resolved),
        bg: resolved,
        title: 'Saved color',
      },
    ];
    saveCustomThemeColors(this.custom);
  }

  private applyHsv(h: number, s: number, v: number, syncStop = true): void {
    this.h = h;
    this.s = s;
    this.v = v;
    if (syncStop && this.mode === 'gradient') {
      this.writeActiveStop(hsvToHex(h, s, v));
    }
  }

  private syncHexFromHsv(): void {
    this.hexInput = hsvToHex(this.h, this.s, this.v).slice(1);
  }

  private updateSv(event: PointerEvent): void {
    if (!this.dragEl) {
      return;
    }
    const r = this.dragEl.getBoundingClientRect();
    const s = Math.min(1, Math.max(0, (event.clientX - r.left) / r.width));
    const v = Math.min(1, Math.max(0, 1 - (event.clientY - r.top) / r.height));
    this.applyHsv(this.h, s, v);
    this.syncHexFromHsv();
  }

  private updateHue(event: PointerEvent): void {
    if (!this.dragEl) {
      return;
    }
    const r = this.dragEl.getBoundingClientRect();
    const h = Math.min(1, Math.max(0, (event.clientX - r.left) / r.width)) * 360;
    this.applyHsv(h, this.s, this.v);
    this.syncHexFromHsv();
  }

  private endDrag(): void {
    this.dragKind = null;
    this.dragEl = null;
  }
}
